import {createContext, use, useCallback, useMemo, useRef, useState, type ReactNode} from 'react'

export interface ImplicitDismissals {
  /** The dismissed keys as of this render, for deciding what to draw. */
  dismissed: ReadonlySet<string>
  /**
   * The dismissed keys as of now. Submit reads this rather than what its render
   * closed over, so a chip removed while a slow page resolution was in flight
   * still leaves the turn.
   */
  peek: () => ReadonlySet<string>
  dismiss: (keys: readonly string[]) => void
}

const ImplicitDismissalsContext = createContext<ImplicitDismissals | null>(null)

function useDismissalState(): ImplicitDismissals {
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set())
  const dismissedRef = useRef(dismissed)

  const dismiss = useCallback((keys: readonly string[]) => {
    if (keys.length === 0) return
    const next = new Set(dismissedRef.current)
    for (const key of keys) next.add(key)
    if (next.size === dismissedRef.current.size) return
    dismissedRef.current = next
    setDismissed(next)
  }, [])

  const peek = useCallback(() => dismissedRef.current, [])

  return useMemo(() => ({dismissed, peek, dismiss}), [dismissed, peek, dismiss])
}

/**
 * Shares which implicit attachments the user has dropped, and which ones a turn
 * has already carried, across every composer under it.
 *
 * A conversation is composed on two different surfaces: the new-session
 * composer before a session exists, and the established composer after. They
 * mount and unmount around each other — the panel swaps surfaces on the first
 * turn, and unmounts the composer entirely to show history — so a decision held
 * inside either one is lost the moment the other takes over. Holding it here
 * instead is what makes "don't attach this page" and "this page has already
 * been sent" stick for the whole conversation rather than for one mount.
 */
export function ImplicitDismissalsProvider({children}: {children: ReactNode}) {
  const value = useDismissalState()
  return <ImplicitDismissalsContext value={value}>{children}</ImplicitDismissalsContext>
}

/**
 * Reads the shared dismissals, falling back to state local to the caller when
 * no provider is present. The fallback keeps a host that never opted in
 * behaving exactly as it did before the shared store existed.
 */
export function useImplicitDismissals(): ImplicitDismissals {
  const shared = use(ImplicitDismissalsContext)
  const local = useDismissalState()
  return shared ?? local
}
